import React, { useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import { LTIProvider, useLTI } from './contexts/LTIContext';

// Canvas Components
import StudentCertificateView from './components/canvas/StudentCertificateView';
import ProfessorDashboard from './components/canvas/ProfessorDashboard';
import SignatureUpload from './components/canvas/SignatureUpload';
import AdminPanel from './components/canvas/AdminPanel';
import TeachingAssistantView from './components/canvas/TeachingAssistantView';
import RestrictedAccessView from './components/canvas/RestrictedAccessView';

function AppRoutes() {
  const { 
    ltiUser, 
    isInstructor, 
    isStudent, 
    isAdmin, 
    isTeachingAssistant,
    isDesigner,
    isObserver,
    hasContext,
    isAccountNavigation,
    loading 
  } = useLTI();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && ltiUser) {
      // Debug: Ver datos del usuario
      console.log('[App.js] LTI User Data:', {
        is_admin: ltiUser?.is_admin,
        is_account_navigation: ltiUser?.is_account_navigation,
        is_instructor: ltiUser?.is_instructor,
        is_student: ltiUser?.is_student,
        is_ta: ltiUser?.is_ta,
        is_designer: ltiUser?.is_designer,
        is_observer: ltiUser?.is_observer,
        context_id: ltiUser?.context_id,
        placement: ltiUser?.placement,
        roles: ltiUser?.roles
      });
      
      // Redirigir automáticamente según el rol del usuario
      const currentPath = window.location.pathname;
      
      if (currentPath === '/') {
        // Lógica de redirección:
        // 1. Si viene desde account_navigation (admin sin roles de curso) → Panel administración
        // 2. Si viene desde un curso CON rol → Vista según rol del curso
        // 3. Si viene desde un curso SIN rol → Vista restringida
        
        console.log('[App.js] Evaluating navigation:', {
          isAccountNavigation: isAccountNavigation(),
          hasContext: hasContext(),
          isInstructor: isInstructor(),
          isTeachingAssistant: isTeachingAssistant(),
          isStudent: isStudent(),
          isDesigner: isDesigner(),
          isObserver: isObserver(),
          isAdmin: isAdmin()
        });
        
        // PRIMERO: Verificar si es acceso desde Site Admin
        // El backend marca is_account_navigation=true solo si:
        // - Es admin SIN roles de curso (instructor/student/ta)
        if (isAccountNavigation()) {
          // Admin accediendo desde administración del sitio → Panel admin
          console.log('[App.js] Navigating to /canvas/admin (is_account_navigation=true)');
          navigate('/canvas/admin', { replace: true });
        }
        // SEGUNDO: Si tiene contexto de curso, validar roles EN ESE CURSO
        else if (hasContext()) {
          // Está en un curso, verificar qué rol tiene
          if (isInstructor()) {
            console.log('[App.js] Navigating to /canvas/professor/dashboard (instructor in course)');
            navigate('/canvas/professor/dashboard', { replace: true });
          } else if (isTeachingAssistant()) {
            console.log('[App.js] Navigating to /canvas/teaching-assistant (TA in course)');
            navigate('/canvas/teaching-assistant', { replace: true });
          } else if (isStudent()) {
            console.log('[App.js] Navigating to /canvas/student/certificate (student in course)');
            navigate('/canvas/student/certificate', { replace: true });
          } else if (isDesigner() || isObserver()) {
            console.log('[App.js] Navigating to /canvas/restricted (designer/observer in course)');
            navigate('/canvas/restricted', { replace: true });
          } else {
            // En un curso pero sin ningún rol válido
            console.log('[App.js] Navigating to /canvas/restricted (no valid role in course)');
            navigate('/canvas/restricted', { replace: true });
          }
        }
        // TERCERO: Caso por defecto (sin contexto y sin ser admin desde site admin)
        else {
          console.log('[App.js] Navigating to /canvas/restricted (default - no context, no admin access)');
          navigate('/canvas/restricted', { replace: true });
        }
      }
    }
  }, [ltiUser, loading, isInstructor, isStudent, isAdmin, isTeachingAssistant, isDesigner, isObserver, hasContext, isAccountNavigation, navigate]);

  if (loading) {
    return (
      <Box minHeight="100vh" bg="evo.gray" display="flex" alignItems="center" justifyContent="center">
        <Box>Cargando...</Box>
      </Box>
    );
  }

  return (
    <Routes>
      {/* Canvas Routes - Solo acceso via LTI */}
      <Route path="/" element={<Box>Cargando...</Box>} />
      <Route path="/canvas/student/certificate" element={<StudentCertificateView />} />
      <Route path="/canvas/professor/dashboard" element={<ProfessorDashboard />} />
      <Route path="/canvas/teaching-assistant" element={<TeachingAssistantView />} />
      <Route path="/canvas/restricted" element={<RestrictedAccessView />} />
      <Route path="/canvas/signature" element={<SignatureUpload />} />
      <Route path="/canvas/admin" element={<AdminPanel />} />
    </Routes>
  );
}

function App() {
  return (
    <LTIProvider>
      <Box minHeight="100vh" bg="evo.gray">
        <AppRoutes />
      </Box>
    </LTIProvider>
  );
}

export default App;
